import { Stack } from '@mui/material'
import { useSelector } from 'react-redux'

import { useBuildProgressModal } from '@/components/modals/BuildProgressModal'
import { useCopyOrdersModal } from '@/components/modals/CopyOrdersModal'
import { useRouteOptimizationModal } from '@/components/modals/RouteOptimizationModal'
import { Tooltip } from '@/components/primitives/Tooltip'
import { ReadOnlyTooltip } from '@/components/smartUtils/plan/ReadOnlyTooltip'
import { StorageIndicator } from '@/components/StorageIndicator'
import { selectOrderStepIdsInRange } from '@/features/domain/order'
import { useCanAddNewOrder, useIsSimulation, useOrdersLimit } from '@/hooks'

import { useActions } from '../hooks/useActions'
import { StyledCopyIcon } from '../hooks/useStyles'
import { useTexts } from '../useTexts'

import { AddOrderButton } from './AddOrderButton'
import { BuildRoutesButton } from './BuildRoutesButton'
import { Separator } from './Separator'
import { SidebarRowButton } from './SidebarRowButton'

export function Simulation() {
  const { show: showBuildProgressModal, Modal: BuildProgressModal } = useBuildProgressModal()
  const { show: showCopyOrdersModal, Modal: CopyOrdersModal } = useCopyOrdersModal()
  const { show: showRouteOptimizationModal, Modal: RouteOptimizationModal } =
    useRouteOptimizationModal()
  const { addOrder, buildRoutes, customBuild, exportToOperations } = useActions(
    showBuildProgressModal,
    showCopyOrdersModal,
    showRouteOptimizationModal,
  )

  const orderStepIdsInRange = useSelector(selectOrderStepIdsInRange)
  const canAddNewOrder = useCanAddNewOrder()
  const isSimulation = useIsSimulation()
  const ordersLimit = useOrdersLimit()
  const texts = useTexts()

  const gotOrdersToCopy = Object.keys(orderStepIdsInRange).length > 0
  return (
    <Stack marginTop={2} padding="0 24px 24px 16px" spacing={1} width="100%">
      <ReadOnlyTooltip
        placement="right"
        canEdit={canAddNewOrder}
        title={texts.addOrderDisabledTooltipTitle(isSimulation)}
        subtitle={texts.addOrderDisabledTooltipSubtitle(isSimulation, ordersLimit)}
        render={preventEditing => (
          <AddOrderButton
            disabled={preventEditing}
            onAddOrder={addOrder}
            testid="sidebar-simulations__add-order-button"
          />
        )}
      />

      <Separator />

      <BuildRoutesButton
        onInstantBuild={buildRoutes}
        onCustomBuild={customBuild}
        testid="sidebar-simulations__build-button"
      />

      <Separator />

      <Tooltip
        subtitle={texts.emptyPlanSubtitle}
        title={texts.emptyPlanTitle}
        disabled={gotOrdersToCopy}
        placement="right"
      >
        <SidebarRowButton
          disabled={!gotOrdersToCopy}
          onClick={exportToOperations}
          label={texts.copyToOperations}
          icon={<StyledCopyIcon color={gotOrdersToCopy ? undefined : 'disabled'} />}
          testid="sidebar-simulations__copy-button"
        />
      </Tooltip>

      <StorageIndicator />

      <BuildProgressModal />
      <CopyOrdersModal />
      <RouteOptimizationModal />
    </Stack>
  )
}
